import { Suspense, lazy, useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigationType } from 'react-router-dom';
import Header from '@/shared/features/header/presentation/components/Header';
import NewHomeHeader from '@/features/newHome/presentation/components/NewHomeHeader';
import Footer from '@/shared/features/footer/presentation/components/Footer';
import { NavigationLoaderProvider } from '@/shared/features/navigation-loader';
import {
  commonFacebookEventsUtil,
  getConnectoId,
} from '@/shared/features/analytics/utils/facebookCommonAttributes';
import { SmartBannerWidget } from '@/shared/widgets/SmartBannerWidget';
import { AccountMergingEntry } from '@/shared/features/account-merging/presentation/components/AccountMergingEntry';
import { VerifyPhoneNumberEntry } from '@/shared/features/phone-verification/presentation/components/VerifyPhoneNumberEntry';
import { useUserContext } from '@/shared/features/user-context/presentation/hooks/useUserContext';

function ScrollToTopOnNavigate() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const prevPathnameRef = useRef(location.pathname);

  useEffect(() => {
    const prevPathname = prevPathnameRef.current;
    if (prevPathname === location.pathname) {
      return;
    }
    prevPathnameRef.current = location.pathname;
    if (navigationType === 'POP') return;
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [location.pathname, navigationType]);

  return null;
}

import { usePopupHistory } from '@/shared/hooks/usePopupHistory';
import { useAppSelector, useAppDispatch } from '@/app/store/store';
import { openLoginPopup, closeLoginPopup } from '@/app/store/loginUiSlice';
import { SoftLoginEntry } from '@/features/soft-login/presentation/components/SoftLoginEntry';
import { useIsApp } from '@/core/hooks/useIsApp';

const LazyLoginPopup = lazy(async () => {
  const mod = await import('@/features/login/presentation/components/LoginPopup');
  return { default: mod.LoginPopup };
});

import { useMultivariateExperiment } from '@/shared/features/multivariateexperiment/presentation/hooks/useMultivariateExperiment';

export default function Layout() {
  const dispatch = useAppDispatch();
  const loginOpen = useAppSelector((s) => s.loginUi.isLoginOpen);
  const loginInitialView = useAppSelector((s) => s.loginUi.initialView);
  const { homePageVariant: homeVariant } = useMultivariateExperiment();
  const location = useLocation();
  const isApp = useIsApp();
  const { isLoading: isContextLoading } = useUserContext();
  const pageViewPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (isContextLoading) return;
    if (pageViewPathRef.current === location.pathname) return;
    pageViewPathRef.current = location.pathname;
    const connectoId = getConnectoId();
    commonFacebookEventsUtil(
      'PageView',
      connectoId ? { CONNECTO_ID: connectoId } : {},
    );
  }, [location.pathname, isContextLoading]);

  const { goBack } = usePopupHistory({
    isOpen: loginOpen,
    onClose: () => dispatch(closeLoginPopup()),
    stateKey: 'loginPopup',
  });

  const onOpenLogin = (mode?: 'login' | 'signup') => {
    dispatch(openLoginPopup(mode === 'signup' ? 'signup' : 'otp'));
  };

  const isNewHomePage = (homeVariant === 'V2' || homeVariant === 'V3' || homeVariant === 'V4' || homeVariant === 'V5' || homeVariant === 'V6') && location.pathname === '/';

  return (
    <NavigationLoaderProvider>
      <div className="min-h-screen flex flex-col">

        {!isApp && (isNewHomePage ? (
          <NewHomeHeader onOpenLogin={onOpenLogin} />
        ) : (
          <Header onOpenLogin={onOpenLogin} />
        ))}
        <SmartBannerWidget />
        <main className="relative z-[49] md:z-auto flex-1">
          <ScrollToTopOnNavigate />
          <Outlet />
        </main>

        {!isApp && <Footer />}

        <Suspense fallback={null}>
          <LazyLoginPopup
            open={loginOpen}
            initialView={loginInitialView}
            onOpenChange={(open) => (open ? dispatch(openLoginPopup()) : goBack())}
            onCloseWithHistory={goBack}
          />
        </Suspense>
        <AccountMergingEntry />
        <VerifyPhoneNumberEntry />
        <SoftLoginEntry />
      </div>
    </NavigationLoaderProvider>
  );
}

